import {
  ConsumptionTileElementPLP,
  DynamicWrapper,
  EnvKConsumptionAndEmissions,
  getRelatedProduct,
  useServicesContext,
  VehicleBasic,
} from '@oneaudi/vtp-shared';
import React from 'react';
import {
  AlphaOnlyWrapper,
  ConsumptionEmissionContainer,
  LinkButtonContainer,
} from './ActionButtons.styles';
import { TextButton } from '@oneaudi/unified-web-components';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { FeatureAppLoader } from '@feature-hub/react';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { trackLayerClose, trackLayerLinkClick, trackLayerOpen } from '../../tracking/tilesTracking';
import { GfaLayerManagerV1 } from '@volkswagen-onehub/gfa-layer-manager';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { useI18n } from '@oneaudi/i18n-context';

interface Props {
  vehicle: VehicleBasic;
}

const ConsumptionAndEmission: React.FC<Props> = ({ vehicle }) => {
  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;
  const localeService = useServicesContext().getAdditionalService(
    'gfa:locale-service'
  ) as LocaleServiceV1;
  const layerManager = useServicesContext().getAdditionalService(
    'gfa:layer-manager'
  ) as GfaLayerManagerV1;
  const getConfigurationObj = useServicesContext().getConfiguration();
  const scStartPageUrl = getConfigurationObj?.urls?.scStartPageLink;
  const consumptionVersion = getConfigurationObj?.featureApps?.consumptionVersion || '4.3.0';
  const consumptionLayerLabel = useI18n({ id: 'nemo.ui.sc.tiles.consumption.link.text' });
  const isDEMarket = localeService.countryCode?.toLowerCase() === 'de';
  const relatedProduct = getRelatedProduct(vehicle);
  const openLayer = () => {
    layerManager?.openFocusLayer(
      () => (
        <div className="tileLayer">
          <div data-fefa-custom-id="tileConsumption">
            <FeatureAppLoader
              featureAppId="tileConsumption"
              src={`https://fa-vtp-consumption.cdn.prod.arcade.apps.one.audi/${consumptionVersion}/fh/app.js`}
              config={{
                vehicleId: vehicle.id,
                eecImageUrl: `https://fa-vtp-consumption.cdn.prod.arcade.apps.one.audi/${consumptionVersion}/static`,
              }}
            />
          </div>
          <FeatureAppLoader
            featureAppId="tile-footnotes"
            src="https://fa-footnote-engine.cdn.prod.arcade.apps.one.audi/v2.4.8/fh/app.js"
            config={{
              apiUrls: [
                `${scStartPageUrl}/commons/sc_footnotes/_jcr_content/content/sc_footnotes.json`,
              ],
              omitSideSpacing: true,
              omitBottomSpacing: true,
              uiTheme: 'light',
              layerElementClassName: 'tileLayer',
            }}
          />
        </div>
      ),
      {},
      {
        size: FocusLayerSizeV2.A,
        userCloseable: true,
        onClose: () => {
          if (trackingService) {
            trackLayerClose(trackingService, 'vtp emission layer');
          }
        },
      }
    );
    trackLayerLinkClick(trackingService, consumptionLayerLabel, 'text link', relatedProduct);
    trackLayerOpen(trackingService, 'emission layer', 'vtp emission layer', undefined);
  };
  const hasConsumptionValues = (veh: VehicleBasic | undefined) => {
    return (
      veh?.io?.hasWltp ||
      veh?.io?.hasNedc ||
      veh?.vlsEnergyProvision?.hasWltp ||
      veh?.vlsEnergyProvision?.hasNedc
    );
  };
  return (
    <DynamicWrapper AlphaWrapperFc={AlphaOnlyWrapper}>
      {isDEMarket && (
        <div>
          <EnvKConsumptionAndEmissions vehicle={vehicle} data-testid="pkv-envkv" isPlp />
        </div>
      )}
      {!isDEMarket && hasConsumptionValues(vehicle) && (
        <ConsumptionEmissionContainer>
          <ConsumptionTileElementPLP
            vehicleId={vehicle.id}
            order={['consumption', 'emission']}
            displayedItems={['emission', 'consumption']}
            hideLayerBtn
            isPlp
            data-testid="consumptionElement"
          />
          <LinkButtonContainer>
            <TextButton
              variant="tertiary"
              size="sm"
              icon="forward"
              iconPosition="trailing"
              onClick={openLayer}
              data-testid="consumptionLayer"
            >
              {consumptionLayerLabel}
            </TextButton>
          </LinkButtonContainer>
        </ConsumptionEmissionContainer>
      )}
    </DynamicWrapper>
  );
};
export default ConsumptionAndEmission;
